/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { addressesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const EMPTY = { label: 'Home', full_name: '', address: '', city: '', postal_code: '', country: '', is_default: false };

const AddressManager = ({ onChange }) => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const data = await addressesApi.list();
      setAddresses(data);
      onChange?.(data);
    } catch (e) {
      setErr('Failed to load addresses');
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const openNew = () => setEditing({ ...EMPTY, full_name: user?.name || '' });
  const openEdit = (a) => setEditing({ ...a });
  const closeForm = () => { setEditing(null); setErr(''); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      if (editing.id) await addressesApi.update(editing.id, editing);
      else await addressesApi.create(editing);
      await load();
      closeForm();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    await addressesApi.remove(id);
    await load();
  };

  const makeDefault = async (id) => {
    await addressesApi.setDefault(id);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">SAVED ADDRESSES</div>
        <button onClick={openNew} className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] border-b border-[#1a1410] pb-0.5">
          <Plus className="w-4 h-4" strokeWidth={1.4} /> ADD ADDRESS
        </button>
      </div>

      {err && <div className="mt-4 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

      {addresses.length === 0 ? (
        <p className="mt-6 text-sm text-[#7a6a5a]">No addresses yet. Add one to speed up checkout.</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.id} className={`border p-5 ${a.is_default ? 'border-[#1a1410]' : 'border-[#cdc2b1]'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#7a6a5a]">{a.label || 'Home'}</div>
                  <div className="font-serif text-lg mt-1">{a.full_name}</div>
                  <div className="text-sm text-[#3a2e26] mt-1 leading-snug">
                    {a.address}<br />
                    {a.city}, {a.postal_code}<br />
                    {a.country}
                  </div>
                </div>
                {a.is_default && (
                  <span className="text-[9px] tracking-[0.22em] bg-[#1a1410] text-[#f5f1e6] px-2 py-1">DEFAULT</span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-4 text-[11px] tracking-[0.22em]">
                <button onClick={() => openEdit(a)} className="inline-flex items-center gap-1 hover:text-black">
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.4} /> EDIT
                </button>
                {!a.is_default && (
                  <button onClick={() => makeDefault(a.id)} className="inline-flex items-center gap-1 hover:text-black">
                    <Check className="w-3.5 h-3.5" strokeWidth={1.4} /> SET DEFAULT
                  </button>
                )}
                <button onClick={() => remove(a.id)} className="inline-flex items-center gap-1 text-[#7a3a2a] hover:text-[#5a1a0a] ml-auto">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.4} /> DELETE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-[#f5f1e6] w-full max-w-lg">
            <div className="flex items-center justify-between px-6 h-16 border-b border-[#e5dfd0]">
              <div className="text-[11px] tracking-[0.28em]">{editing.id ? 'EDIT ADDRESS' : 'NEW ADDRESS'}</div>
              <button onClick={closeForm}><X className="w-5 h-5" strokeWidth={1.4} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-5">
              <Field label="LABEL" value={editing.label} onChange={(v) => setEditing({ ...editing, label: v })} required={false} />
              <Field label="FULL NAME" value={editing.full_name} onChange={(v) => setEditing({ ...editing, full_name: v })} />
              <Field label="ADDRESS" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} />
              <div className="grid grid-cols-2 gap-5">
                <Field label="CITY" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} />
                <Field label="POSTAL CODE" value={editing.postal_code} onChange={(v) => setEditing({ ...editing, postal_code: v })} />
              </div>
              <Field label="COUNTRY" value={editing.country} onChange={(v) => setEditing({ ...editing, country: v })} />
              <label className="flex items-center gap-2 text-[11px] tracking-[0.22em]">
                <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} />
                SET AS DEFAULT
              </label>
              {err && <div className="p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={busy} className="flex-1 bg-[#1a1410] text-[#f5f1e6] py-3 text-[11px] tracking-[0.32em] hover:bg-black disabled:opacity-50">
                  {busy ? 'SAVING…' : 'SAVE ADDRESS'}
                </button>
                <button type="button" onClick={closeForm} className="px-6 py-3 text-[11px] tracking-[0.32em] border border-[#cdc2b1]">CANCEL</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, required = true }) => (
  <div>
    <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">{label}</label>
    <input value={value || ''} onChange={(e) => onChange(e.target.value)} required={required}
      className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
  </div>
);

export default AddressManager;
